import type { MultiTimeframeEntry } from '~/state/useMultiTimeframeState'
import { useTimeframeLabel } from './useTimeframeLabel'
import type { ReviewPageState } from './useReviewPageState'
import { usePageControls } from '~/api/hooks/useCharts'
import { useDimCatalog } from '~/api/hooks/useCatalog'
import {
  resolvedCampaignFilters,
  mergeArmFilters,
  ALL_CAMPAIGN_DIMS,
  CAMPAIGN_EXACT_DIM,
} from '~/state/campaignPredicate'
import { dimNamesOf } from '~/state/useCampaignState'
import type { CampaignTimeslotItem } from '~/api/endpoints/getControls'
import { stripToFactDims } from './stripToFactDims'
import { CARDS } from '~/components/Charts/chrome/chartChrome'

/** Structurally identical to FanoutTimeframe (useTimeframeFanout) and DynamicsTimeframe
 *  (useDynamicsPanels) — one resolved shape serves both consumers. */
export interface ResolvedTimeframe {
  id: string
  label: string
  from: string
  to: string
  /** Campaigns C1 Task 7: a campaign arm's merged+fact-pruned predicate — consumed by
   *  useTimeframeFanout/useDynamicsPanels as a per-tf filter_dims override. Absent for the
   *  main arm and for entries with no campaign or an unresolvable one. */
  filter_dims?: Record<string, string[]>
  /** C3 fix-wave (T3-fold): the entry's OWN campaign id, set in the SAME branch as filter_dims
   *  (both present together, both absent together) — lets a per-fact re-resolution site (the
   *  driver-arm sites in MetricDriverBarsTile/DynamicsPanelsTile) match an arm by its own id
   *  instead of re-deriving it positionally against state.multiTimeframe.entries (a hazard if
   *  the two arrays ever drift/reorder). NOT part of the frozen pin wire shape — a live,
   *  same-session lookup aid only; buildPinV1 call sites strip it before freezing
   *  viz_params.timeframes (a saved pin replays id/label/from/to/filter_dims, nothing else). */
  campaignId?: string
}

/** Campaigns C1 Task 7: resolves an entry's campaign id against controls. Returns the FINAL
 *  merged (page filters with managed dims replaced by the predicate) AND fact-pruned filter_dims
 *  — the builder applies it verbatim, with no merge/prune logic of its own (kept pure/generic). */
export type CampaignResolver = (
  id: string,
) => { predicate: Record<string, string[]>; label: string } | null

/** Campaigns C3 (spec D-C3-1): resolves ONE campaign arm's filter_dims for a GIVEN fact — merge
 *  (mergeArmFilters strips every campaign-owned dim from `base` then applies the fact-aware
 *  resolution: the exact `Campaign` dim on a carrying fact, attribution filters otherwise) +
 *  request-prune to that fact's own dim catalog. Pure + exported so every per-fact caller shares
 *  the ONE rule instead of re-deriving it: the metric resolver below (state.factTable), and the
 *  driver-arm sites in MetricDriverBarsTile/DynamicsPanelsTile (the driver fact, independently). */
export function resolveArmFilters(
  item: CampaignTimeslotItem,
  factCarriesCampaign: boolean,
  base: Record<string, string[]>,
  factDims: Set<string>,
): Record<string, string[]> {
  const merged = mergeArmFilters(base, resolvedCampaignFilters(item, factCarriesCampaign))
  return stripToFactDims(merged, [], factDims).filters
}

/** Row 16 (v2 parity): the main window IS the first timeframe — v2 modeled it as campaigns[0]
 *  and rendered all entries; v3 used to render tf_list only (1 pick ⇒ a single lonely bar).
 *  Pure builder: returns null when the picker is empty so callers keep their tile-specific
 *  single-mode fallback (MetricDriverBars: [main]; DynamicsPanels: [] = sub-period path).
 *
 *  Campaigns C1 Task 7 (spec §6): an entry carrying a campaign id resolves via `resolveCampaign`
 *  — success merges the arm's predicate into `filter_dims`; an unresolvable id (re-cut vintage)
 *  degrades to window-only (no filter_dims, label suffix) + a loud console.warn
 *  (no-silent-fallbacks). The main arm NEVER gets explicit filter_dims — it inherits the page's
 *  effective filters, which already carry a main campaign's materialized predicate if one is set. */
export function buildMultiTfTimeframes(
  entries: MultiTimeframeEntry[],
  main: { label: string; from: string; to: string },
  resolveCampaign?: CampaignResolver,
): ResolvedTimeframe[] | null {
  if (entries.length === 0) return null
  return [
    { id: 'main', label: main.label, from: main.from, to: main.to },
    // Picked ids keep their own indices — 'main' cannot collide with `${label}-${i}`.
    ...entries.map((e, i) => {
      const id = `${e.label}-${i}`
      if (!e.campaign) return { id, label: e.label, from: e.from, to: e.to }
      const resolved = resolveCampaign?.(e.campaign)
      if (!resolved) {
        console.warn(
          `multi-tf: campaign ${e.campaign} not found in controls; rendering window only`,
        )
        return { id, label: `${e.label}${CARDS.windowOnlySuffix}`, from: e.from, to: e.to }
      }
      return {
        id,
        label: resolved.label,
        from: e.from,
        to: e.to,
        filter_dims: resolved.predicate,
        // T3-fold: carried alongside filter_dims (same branch) so per-fact re-resolution sites
        // can match this arm by id instead of re-deriving it positionally.
        campaignId: e.campaign,
      }
    }),
  ]
}

/** Hook wrapper: main label via useTimeframeLabel (preset/frame label, else ISO range) —
 *  the first bar reads "Last 12 Weeks", not a raw date pair. Unconditional hook call.
 *
 *  Campaigns C3 (spec D-C3-1): supplies the resolver closure — resolveArmFilters (fact-aware
 *  resolve + merge + request-prune, mirroring pruneDimsToFact's filters handling exactly against
 *  the active fact's dim catalog) — so the builder itself stays free of merge/prune concerns. */
export interface MultiTfResult {
  timeframes: ResolvedTimeframe[] | null
  /** FU-02(b): an arm NAMES a campaign but the ratio-pair machinery is still PENDING —
   *  consumers hold every walk fed by these arms. Dynamics consumers gate `enabled` (dual-mode:
   *  enabled+[] fires the single-window walk); fanout consumers pass []. Settled error ⇒ false
   *  (fallback resolution — visible degradation, never a permanent hold). */
  pending: boolean
}

export function useMultiTfTimeframes(state: ReviewPageState): MultiTfResult {
  const mainLabel = useTimeframeLabel(state)
  const controls = usePageControls(state.factTable)
  const dimCatalog = useDimCatalog()
  // Campaigns C3b (spec D-C3b-1): on a ratio page every arm fires its OWN walk_ratio over the SAME
  // component-fact pair, so the exact-vs-attribution decision and the catalog prune are FACT-level
  // — identical for every arm (only the item differs). Read both component facts' controls to
  // derive the shared bothCarry flag + union prune. The pair itself comes off state (threaded from
  // useReviewPageState's registry-resolved ratio_kpi); re-running usePageCharts/useRatioComponentFacts
  // here would only re-derive the same value AND break sibling tile tests that mock only
  // usePageControls. usePageControls is enabled-gated on non-null, so the non-ratio path fires none.
  const numControls = usePageControls(state.ratioComponents?.numFact ?? null)
  const denomControls = usePageControls(state.ratioComponents?.denomFact ?? null)
  // Same "pass everything through while loading" fallback used for the driver-arm strip
  // elsewhere (MetricDriverBarsTile/DynamicsPanelsTile) — a not-yet-loaded catalog must not
  // prematurely prune a predicate before the real fact-dim set is known. ALL_CAMPAIGN_DIMS (not
  // just the managed ones) since a carrying fact's resolution can now also produce the exact
  // Campaign key.
  const factDims: Set<string> = dimCatalog.data
    ? new Set(
        dimCatalog.data.filter((r) => r.fact_table === state.factTable).map((r) => r.dim_name),
      )
    : new Set([...Object.keys(state.baseParams.filter_dims), ...ALL_CAMPAIGN_DIMS])
  // C3: an arm can name a campaign while none is selected as the page's base (state.campaign
  // null) — derive the page's own fact's carrying flag straight from controls in that case, via
  // the same dimNamesOf source useCampaignState trusts (not re-derived ad hoc here).
  const factCarriesForPage = dimNamesOf(controls.data).has(CAMPAIGN_EXACT_DIM)

  // Ratio resolution is READY only once BOTH component controls resolve; until then the arm
  // resolver falls back to the single page-fact path (the transient one-sided form, mirroring
  // useCampaignState/T3's stance), so no arm is ever pruned to an empty union mid-load. dimNamesOf
  // reads dims_view — the ONE trusted carrying-flag source across the codebase.
  const ratioReady =
    state.ratioComponents != null &&
    numControls.data !== undefined &&
    denomControls.data !== undefined
  const numDims = ratioReady ? dimNamesOf(numControls.data) : null
  const denomDims = ratioReady ? dimNamesOf(denomControls.data) : null
  const ratioBothCarry =
    ratioReady && numDims!.has(CAMPAIGN_EXACT_DIM) && denomDims!.has(CAMPAIGN_EXACT_DIM)
  const ratioUnionDims = ratioReady ? new Set<string>([...numDims!, ...denomDims!]) : null

  const resolveCampaign: CampaignResolver = (id) => {
    const item = controls.data?.campaign_timeslots?.items.find((i) => i.value === id)
    if (!item) return null
    // C3b: on a ratio page every arm uses the pair rule (bothCarry + union) — never the single
    // page-fact flag, which would send an exact {Campaign:[id]} through a walk_ratio whose other
    // side doesn't carry the dim (the D0 defect). resolveArmFilters' body is unchanged: passing
    // factCarriesCampaign := bothCarry makes resolvedCampaignFilters produce the exact ratio rule
    // (bothCarry ? exact : attribution). Non-ratio pages and the not-yet-ready transient keep the
    // existing single-fact resolution.
    const factCarriesCampaign = ratioReady
      ? ratioBothCarry
      : (state.campaign?.factCarriesCampaign ?? factCarriesForPage)
    const dimsForPrune = ratioReady ? ratioUnionDims! : factDims
    const pruned = resolveArmFilters(
      item,
      factCarriesCampaign,
      state.baseParams.filter_dims,
      dimsForPrune,
    )
    return { predicate: pruned, label: item.label }
  }

  // FU-02(b): an arm naming a campaign must not fire a one-sided walk while the ratio-pair
  // machinery is still in flight. `pending` short-circuits off armNamesCampaign — non-ratio pages
  // and no-campaign-arm cases are byte-identical (false). The `state.ratioComponents != null`
  // guard sits INSIDE the parenthesis because num/denom controls are enabled-gated on that pair —
  // a disabled v5 query reports isPending:true, so without the guard a non-ratio page would hold.
  const armNamesCampaign = (state.multiTimeframe?.entries ?? []).some((e) => Boolean(e.campaign))
  const pending =
    armNamesCampaign &&
    (state.ratioMapPending ||
      (state.ratioComponents != null && (numControls.isPending || denomControls.isPending)))
  return {
    timeframes: buildMultiTfTimeframes(
      state.multiTimeframe?.entries ?? [],
      { label: mainLabel, from: state.fromIso, to: state.toIso },
      resolveCampaign,
    ),
    pending,
  }
}
